// Simple in-memory accounting engine for the finance module.

// ============================================================================
// CHART OF ACCOUNTS
// ============================================================================

const CHART_OF_ACCOUNTS = [
  // Assets
  { id: 'cash', name: 'Cash', type: 'asset' },
  { id: 'accounts-receivable', name: 'Accounts Receivable', type: 'asset' },
  { id: 'inventory', name: 'Inventory', type: 'asset' },
  { id: 'equipment', name: 'Equipment', type: 'asset' },
  { id: 'prepaid-expense', name: 'Prepaid Expense', type: 'asset' },
  
  // Liabilities
  { id: 'accounts-payable', name: 'Accounts Payable', type: 'liability' },
  { id: 'notes-payable', name: 'Notes Payable', type: 'liability' },
  { id: 'unearned-revenue', name: 'Unearned Revenue', type: 'liability' },
  
  // Equity
  { id: 'owners-equity', name: "Owner's Equity", type: 'equity' },
  
  // Revenue
  { id: 'sales-revenue', name: 'Sales Revenue', type: 'revenue' },
  { id: 'service-revenue', name: 'Service Revenue', type: 'revenue' },
  
  // Expenses
  { id: 'cogs', name: 'Cost of Goods Sold', type: 'expense' },
];

// ============================================================================
// IN-MEMORY JOURNAL ENTRIES (DEMO DATA)
// ============================================================================

let journalEntries = [
  {
    id: 'JE-0001',
    date: '2025-01-02',
    memo: 'Initial cash investment by owner',
    ref: null,
    lines: [
      { accountId: 'cash', accountName: 'Cash', type: 'debit', amount: 50000 },
      { accountId: 'owners-equity', accountName: "Owner's Equity", type: 'credit', amount: 50000 },
    ],
  },
  {
    id: 'JE-0002',
    date: '2025-01-05',
    memo: 'Purchase equipment for cash',
    ref: 'INV-1001',
    lines: [
      { accountId: 'equipment', accountName: 'Equipment', type: 'debit', amount: 15000 },
      { accountId: 'cash', accountName: 'Cash', type: 'credit', amount: 15000 },
    ],
  },
  {
    id: 'JE-0003',
    date: '2025-01-10',
    memo: 'Purchase inventory on account',
    ref: 'PO-2001',
    lines: [
      { accountId: 'inventory', accountName: 'Inventory', type: 'debit', amount: 8000 },
      { accountId: 'accounts-payable', accountName: 'Accounts Payable', type: 'credit', amount: 8000 },
    ],
  },
  {
    id: 'JE-0004',
    date: '2025-01-15',
    memo: 'Sale of goods for cash',
    ref: 'INV-3001',
    lines: [
      { accountId: 'cash', accountName: 'Cash', type: 'debit', amount: 12000 },
      { accountId: 'sales-revenue', accountName: 'Sales Revenue', type: 'credit', amount: 12000 },
    ],
  },
  {
    id: 'JE-0005',
    date: '2025-01-15',
    memo: 'Record cost of goods sold',
    ref: 'INV-3001',
    lines: [
      { accountId: 'cogs', accountName: 'Cost of Goods Sold', type: 'debit', amount: 5000 },
      { accountId: 'inventory', accountName: 'Inventory', type: 'credit', amount: 5000 },
    ],
  },
  {
    id: 'JE-0006',
    date: '2025-01-20',
    memo: 'Provide services on account',
    ref: 'INV-3002',
    lines: [
      { accountId: 'accounts-receivable', accountName: 'Accounts Receivable', type: 'debit', amount: 3500 },
      { accountId: 'service-revenue', accountName: 'Service Revenue', type: 'credit', amount: 3500 },
    ],
  },
  {
    id: 'JE-0007',
    date: '2025-01-25',
    memo: 'Pay accounts payable',
    ref: 'CHK-1001',
    lines: [
      { accountId: 'accounts-payable', accountName: 'Accounts Payable', type: 'debit', amount: 4000 },
      { accountId: 'cash', accountName: 'Cash', type: 'credit', amount: 4000 },
    ],
  },
  {
    id: 'JE-0008',
    date: '2025-01-28',
    memo: 'Collect accounts receivable',
    ref: 'RCT-1001',
    lines: [
      { accountId: 'cash', accountName: 'Cash', type: 'debit', amount: 3500 },
      { accountId: 'accounts-receivable', accountName: 'Accounts Receivable', type: 'credit', amount: 3500 },
    ],
  },
];

let nextJournalId = 9; // Next sequential ID

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Find account by ID
 */
function findAccount(accountId) {
  return CHART_OF_ACCOUNTS.find(acc => acc.id === accountId);
}

/**
 * Check if a date is within a range (inclusive)
 */
function isDateInRange(date, fromDate, toDate) {
  if (fromDate && date < fromDate) return false;
  if (toDate && date > toDate) return false;
  return true;
}

/**
 * Generate next journal entry ID
 */
function generateJournalId() {
  const id = `JE-${String(nextJournalId).padStart(4, '0')}`;
  nextJournalId++;
  return id;
}

/**
 * Validate that an entry balances
 */
function validateEntry(entry) {
  if (!entry.lines || entry.lines.length === 0) {
    throw new Error('Journal entry must have at least one line');
  }

  let totalDebits = 0;
  let totalCredits = 0;

  for (const line of entry.lines) {
    if (line.amount <= 0) {
      throw new Error(`All amounts must be positive. Invalid amount: ${line.amount}`);
    }

    if (line.type === 'debit') {
      totalDebits += line.amount;
    } else if (line.type === 'credit') {
      totalCredits += line.amount;
    } else {
      throw new Error(`Invalid line type: ${line.type}. Must be 'debit' or 'credit'`);
    }
  }

  // Allow for small floating point differences
  const difference = Math.abs(totalDebits - totalCredits);
  if (difference > 0.01) {
    throw new Error(
      `Journal entry does not balance. Debits: ${totalDebits.toFixed(2)}, Credits: ${totalCredits.toFixed(2)}`
    );
  }

  return true;
}

/**
 * Determine if an account normally has a debit balance
 * Assets and Expenses: debit normal balance
 * Liabilities, Equity, Revenue: credit normal balance
 */
function isDebitNormalBalance(accountType) {
  return accountType === 'asset' || accountType === 'expense';
}

/**
 * Calculate account balance from transactions
 */
function calculateBalance(transactions, accountType) {
  let balance = 0;
  const isDebitNormal = isDebitNormalBalance(accountType);

  for (const tx of transactions) {
    if (tx.debit !== null) {
      balance += isDebitNormal ? tx.debit : -tx.debit;
    }
    if (tx.credit !== null) {
      balance += isDebitNormal ? -tx.credit : tx.credit;
    }
  }

  return balance;
}

// ============================================================================
// EXPORTED FUNCTIONS
// ============================================================================

/**
 * 1) Get the chart of accounts
 */
export function getChartOfAccounts() {
  return [...CHART_OF_ACCOUNTS];
}

/**
 * 2) Get journal entries, optionally filtered by date range
 */
export function getJournalEntries({ fromDate, toDate } = {}) {
  let entries = [...journalEntries];

  // Filter by date range
  if (fromDate || toDate) {
    entries = entries.filter(entry => 
      isDateInRange(entry.date, fromDate, toDate)
    );
  }

  // Sort by date ascending, then by id
  entries.sort((a, b) => {
    if (a.date !== b.date) {
      return a.date.localeCompare(b.date);
    }
    return a.id.localeCompare(b.id);
  });

  return entries;
}

/**
 * 3) Add a new journal entry
 */
export function addJournalEntry(entryInput) {
  // Validate the entry
  validateEntry(entryInput);

  // Create the new entry with an ID
  const newEntry = {
    id: entryInput.id || generateJournalId(),
    date: entryInput.date,
    memo: entryInput.memo || '',
    ref: entryInput.ref || null,
    lines: entryInput.lines.map(line => ({
      accountId: line.accountId,
      accountName: line.accountName,
      type: line.type,
      amount: line.amount,
    })),
  };

  // Add to in-memory array
  journalEntries.push(newEntry);

  return newEntry;
}

/**
 * 4) Get ledger grouped by account
 */
export function getLedger({ fromDate, toDate } = {}) {
  const entries = getJournalEntries({ fromDate, toDate });
  const ledgerMap = {};

  // Initialize ledger for all accounts
  CHART_OF_ACCOUNTS.forEach(account => {
    ledgerMap[account.id] = {
      accountId: account.id,
      accountName: account.name,
      type: account.type,
      rows: [],
    };
  });

  // Process each journal entry
  entries.forEach(entry => {
    entry.lines.forEach(line => {
      const ledgerAccount = ledgerMap[line.accountId];
      if (!ledgerAccount) return;

      ledgerAccount.rows.push({
        date: entry.date,
        memo: entry.memo,
        ref: entry.ref,
        debit: line.type === 'debit' ? line.amount : null,
        credit: line.type === 'credit' ? line.amount : null,
        balance: 0, // Will be calculated below
      });
    });
  });

  // Calculate running balances for each account
  Object.values(ledgerMap).forEach(ledgerAccount => {
    let runningBalance = 0;
    const isDebitNormal = isDebitNormalBalance(ledgerAccount.type);

    ledgerAccount.rows.forEach(row => {
      if (row.debit !== null) {
        runningBalance += isDebitNormal ? row.debit : -row.debit;
      }
      if (row.credit !== null) {
        runningBalance += isDebitNormal ? -row.credit : row.credit;
      }
      row.balance = runningBalance;
    });
  });

  // Return only accounts that have transactions
  return Object.values(ledgerMap).filter(account => account.rows.length > 0);
}

/**
 * 5) Get trial balance as of a specific date
 */
export function getTrialBalance({ asOfDate } = {}) {
  const entries = getJournalEntries({ 
    fromDate: null, 
    toDate: asOfDate 
  });

  const balances = {};

  // Initialize balances for all accounts
  CHART_OF_ACCOUNTS.forEach(account => {
    balances[account.id] = {
      accountId: account.id,
      accountName: account.name,
      type: account.type,
      balance: 0,
    };
  });

  // Calculate balances from entries
  entries.forEach(entry => {
    entry.lines.forEach(line => {
      const account = balances[line.accountId];
      if (!account) return;

      const isDebitNormal = isDebitNormalBalance(account.type);

      if (line.type === 'debit') {
        account.balance += isDebitNormal ? line.amount : -line.amount;
      } else {
        account.balance += isDebitNormal ? -line.amount : line.amount;
      }
    });
  });

  // Format as trial balance (debit or credit column)
  let totalDebits = 0;
  let totalCredits = 0;

  const trialBalanceAccounts = Object.values(balances)
    .filter(account => account.balance !== 0)
    .map(account => {
      const debit = account.balance > 0 ? account.balance : 0;
      const credit = account.balance < 0 ? -account.balance : 0;

      totalDebits += debit;
      totalCredits += credit;

      return {
        accountId: account.accountId,
        accountName: account.accountName,
        type: account.type,
        debit,
        credit,
      };
    });

  return {
    accounts: trialBalanceAccounts,
    totals: {
      totalDebits,
      totalCredits,
    },
  };
}

/**
 * 6) Get income statement for a date range
 */
export function getIncomeStatement({ fromDate, toDate } = {}) {
  const entries = getJournalEntries({ fromDate, toDate });

  const accountBalances = {};

  // Initialize for revenue and expense accounts
  CHART_OF_ACCOUNTS.forEach(account => {
    if (account.type === 'revenue' || account.type === 'expense') {
      accountBalances[account.id] = {
        accountId: account.id,
        accountName: account.name,
        type: account.type,
        balance: 0,
      };
    }
  });

  // Calculate balances
  entries.forEach(entry => {
    entry.lines.forEach(line => {
      const account = accountBalances[line.accountId];
      if (!account) return;

      const isDebitNormal = isDebitNormalBalance(account.type);

      if (line.type === 'debit') {
        account.balance += isDebitNormal ? line.amount : -line.amount;
      } else {
        account.balance += isDebitNormal ? -line.amount : line.amount;
      }
    });
  });

  // Separate revenue and expense accounts
  const revenueAccounts = [];
  const expenseAccounts = [];
  let totalRevenue = 0;
  let totalExpenses = 0;

  Object.values(accountBalances).forEach(account => {
    if (account.type === 'revenue') {
      const amount = account.balance; // Revenue has credit normal balance, so positive balance is revenue
      revenueAccounts.push({
        accountId: account.accountId,
        accountName: account.accountName,
        amount,
      });
      totalRevenue += amount;
    } else if (account.type === 'expense') {
      const amount = account.balance; // Expense has debit normal balance, so positive balance is expense
      expenseAccounts.push({
        accountId: account.accountId,
        accountName: account.accountName,
        amount,
      });
      totalExpenses += amount;
    }
  });

  const netIncome = totalRevenue - totalExpenses;

  return {
    revenueAccounts: revenueAccounts.filter(a => a.amount !== 0),
    expenseAccounts: expenseAccounts.filter(a => a.amount !== 0),
    totalRevenue,
    totalExpenses,
    netIncome,
  };
}

/**
 * Additional helper: Get balance sheet (assets, liabilities, equity)
 */
export function getBalanceSheet({ asOfDate } = {}) {
  const entries = getJournalEntries({ 
    fromDate: null, 
    toDate: asOfDate 
  });

  const accountBalances = {};

  // Initialize for balance sheet accounts
  CHART_OF_ACCOUNTS.forEach(account => {
    if (account.type === 'asset' || account.type === 'liability' || account.type === 'equity') {
      accountBalances[account.id] = {
        accountId: account.id,
        accountName: account.name,
        type: account.type,
        balance: 0,
      };
    }
  });

  // Calculate balances
  entries.forEach(entry => {
    entry.lines.forEach(line => {
      const account = accountBalances[line.accountId];
      if (!account) return;

      const isDebitNormal = isDebitNormalBalance(account.type);

      if (line.type === 'debit') {
        account.balance += isDebitNormal ? line.amount : -line.amount;
      } else {
        account.balance += isDebitNormal ? -line.amount : line.amount;
      }
    });
  });

  // Calculate net income and add to equity
  const incomeStatement = getIncomeStatement({ 
    fromDate: null, 
    toDate: asOfDate 
  });

  // Separate by type
  const assets = [];
  const liabilities = [];
  const equity = [];
  let totalAssets = 0;
  let totalLiabilities = 0;
  let totalEquity = 0;

  Object.values(accountBalances).forEach(account => {
    const amount = account.balance;

    if (account.type === 'asset') {
      if (amount !== 0) {
        assets.push({
          accountId: account.accountId,
          accountName: account.accountName,
          amount,
        });
        totalAssets += amount;
      }
    } else if (account.type === 'liability') {
      if (amount !== 0) {
        liabilities.push({
          accountId: account.accountId,
          accountName: account.accountName,
          amount,
        });
        totalLiabilities += amount;
      }
    } else if (account.type === 'equity') {
      if (amount !== 0) {
        equity.push({
          accountId: account.accountId,
          accountName: account.accountName,
          amount,
        });
        totalEquity += amount;
      }
    }
  });

  // Add retained earnings (net income)
  if (incomeStatement.netIncome !== 0) {
    equity.push({
      accountId: 'retained-earnings',
      accountName: 'Retained Earnings',
      amount: incomeStatement.netIncome,
    });
    totalEquity += incomeStatement.netIncome;
  }

  return {
    assets,
    liabilities,
    equity,
    totalAssets,
    totalLiabilities,
    totalEquity,
  };
}

/* ---------------------------- Utilities --------------------------- */

export const formatMoney = (n) =>
  (Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ============================================================================
// LEGACY COMPATIBILITY EXPORTS
// ============================================================================
// These functions maintain compatibility with the old API used by existing components

/**
 * Legacy normalizeEntry - converts old format to new format
 */
export function normalizeEntry(e = {}) {
  const date = e.date ? new Date(e.date) : new Date();
  const ref = e.ref || "";
  const memo = e.memo || e.description || "";
  const vendor = e.vendor ?? null;
  const transactionType = e.transactionType ?? null;

  let lines = [];
  if (Array.isArray(e.lines) && e.lines.length) {
    lines = e.lines.map((l) => ({
      account: String(l.account ?? "").trim(),
      debit: Number(l.debit || 0),
      credit: Number(l.credit || 0),
    }));
  } else if (e.account || e.debit || e.credit) {
    lines = [
      {
        account: String(e.account ?? "").trim(),
        debit: Number(e.debit || 0),
        credit: Number(e.credit || 0),
      },
    ];
  }

  return { date, ref, memo, vendor, transactionType, lines };
}

/**
 * Legacy computeLedger - converts old-style journal to ledger format
 */
export function computeLedger(journal = []) {
  const byAccount = {};

  for (const raw of journal) {
    const je = normalizeEntry(raw);
    for (const l of je.lines) {
      if (!l.account) continue;
      if (!byAccount[l.account]) byAccount[l.account] = [];
      byAccount[l.account].push({
        date: je.date,
        ref: je.ref,
        memo: je.memo,
        debit: l.debit,
        credit: l.credit,
      });
    }
  }

  // Running totals (debit-positive convention)
  const ledger = {};
  for (const [acct, rows] of Object.entries(byAccount)) {
    let bal = 0;
    const sorted = rows.sort((a, b) => a.date - b.date);
    ledger[acct] = sorted.map((r) => {
      bal += Number(r.debit || 0) - Number(r.credit || 0);
      return { ...r, balance: bal };
    });
  }
  return ledger;
}

/**
 * Legacy computeTrialBalance - builds trial balance from ledger
 */
export function computeTrialBalance(ledger = {}) {
  const trial = [];
  for (const [account, rows] of Object.entries(ledger)) {
    let debitTotal = 0;
    let creditTotal = 0;
    for (const r of rows) {
      debitTotal += Number(r.debit || 0);
      creditTotal += Number(r.credit || 0);
    }
    const balance = debitTotal - creditTotal; // debit-positive
    trial.push({
      account,
      group: determineAccountGroup(account),
      debit: balance > 0 ? balance : 0,
      credit: balance < 0 ? -balance : 0,
    });
  }
  return trial;
}

/**
 * Helper to determine account group from account name
 */
function determineAccountGroup(accountName) {
  const name = accountName.toLowerCase();
  if (name.includes('cash') || name.includes('receivable') || name.includes('inventory') || 
      name.includes('equipment') || name.includes('prepaid') || name.includes('asset')) {
    return 'Assets';
  }
  if (name.includes('payable') || name.includes('note') || name.includes('unearned') || 
      name.includes('liability')) {
    return 'Liabilities';
  }
  if (name.includes('equity') || name.includes('capital') || name.includes('retained')) {
    return 'Equity';
  }
  if (name.includes('revenue') || name.includes('sales') || name.includes('service')) {
    return 'Revenue';
  }
  if (name.includes('expense') || name.includes('cogs') || name.includes('cost')) {
    return 'Expenses';
  }
  return 'Unknown';
}

/**
 * Legacy computeIncomeStatement
 */
export function computeIncomeStatement(tb = []) {
  const rev = tb.filter((a) => a.group === "Revenue");
  const exp = tb.filter((a) => a.group === "Expenses");
  const sumDebits  = (rows) => rows.reduce((s, r) => s + Number(r.debit  || 0), 0);
  const sumCredits = (rows) => rows.reduce((s, r) => s + Number(r.credit || 0), 0);

  const revenue  = sumCredits(rev) - sumDebits(rev);   // credit-normal
  const expenses = sumDebits(exp)  - sumCredits(exp);  // debit-normal
  const net = revenue - expenses;

  return { revenue, expenses, net, detail: { rev, exp } };
}

/**
 * Legacy computeBalanceSheet
 */
export function computeBalanceSheet(tb = [], is = { net: 0 }) {
  const assets = tb.filter((a) => a.group === "Assets");
  const liab   = tb.filter((a) => a.group === "Liabilities");
  const equity = tb.filter((a) => a.group === "Equity");
  const sumDebits  = (rows) => rows.reduce((s, r) => s + Number(r.debit  || 0), 0);
  const sumCredits = (rows) => rows.reduce((s, r) => s + Number(r.credit || 0), 0);

  const totalAssets = sumDebits(assets) - sumCredits(assets);
  const totalLiab   = sumCredits(liab)  - sumDebits(liab);
  const baseEquity  = sumCredits(equity)- sumDebits(equity);

  // Simple retained earnings plug from IS
  const retainedEarnings = Number(is?.net || 0);
  const totalEquity = baseEquity + retainedEarnings;

  return {
    assets: totalAssets,
    liabilities: totalLiab,
    equity: totalEquity,
    detail: {
      Assets: assets,
      Liabilities: liab,
      Equity: equity,
      RetainedEarnings: retainedEarnings,
    },
  };
}

/**
 * Legacy computeCashFlow
 */
export function computeCashFlow(tb = [], is = { net: 0 }) {
  return {
    operating: Number(is?.net || 0),
    investing: 0,
    financing: 0,
    netChange: Number(is?.net || 0),
  };
}

